import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSupportContext } from '../../context/SupportContext';
import axios from 'axios';
import {
  ArrowLeft,
  Building2,
  Lock,
  AlertTriangle,
  Eye,
  Sparkles,
  Settings2,
  Save,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

// Import Section Components
import AgencyProfileSection from '../../components/platform/settings/AgencyProfileSection';
import BillingMembershipSection from '../../components/platform/settings/BillingMembershipSection';
import MyStaffSection from '../../components/platform/settings/MyStaffSection';
import PortalAccessSection from '../../components/platform/settings/PortalAccessSection';
import PortalsMobileAppSection from '../../components/platform/settings/PortalsMobileAppSection';
import BrandingWhiteLabelSection from '../../components/platform/settings/BrandingWhiteLabelSection';
import DomainsRedirectsSection from '../../components/platform/settings/DomainsRedirectsSection';
import CommunicationSettingsSection from '../../components/platform/settings/CommunicationSettingsSection';
import CustomFieldsSection from '../../components/platform/settings/CustomFieldsSection';
import CustomValuesSection from '../../components/platform/settings/CustomValuesSection';
import IntegrationsSection from '../../components/platform/settings/IntegrationsSection';
import PrivateIntegrationsSection from '../../components/platform/settings/PrivateIntegrationsSection';
import AuditLogsSection from '../../components/platform/settings/AuditLogsSection';
import AdvancedLabsSection from '../../components/platform/settings/AdvancedLabsSection';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// Section labels for display
const SECTION_LABELS = {
  'profile': 'Agency Profile',
  'billing': 'Billing & Membership',
  'staff': 'My Staff',
  'portal-access': 'Portal Access',
  'portals-mobile': 'Portals & Mobile App',
  'branding': 'Branding & White Label',
  'domains': 'Domains & Redirects',
  'communication': 'Communication Settings',
  'custom-fields': 'Custom Fields',
  'custom-values': 'Custom Values',
  'integrations': 'Integrations',
  'private-integrations': 'Private Integrations',
  'audit-logs': 'Audit Logs',
  'labs': 'Advanced / Labs',
};

/**
 * Locked Section Overlay
 */
const LockedSectionOverlay = ({ sectionKey, sectionLabel }) => (
  <div className="flex flex-col items-center justify-center py-16 px-8 text-center" data-testid={`locked-section-${sectionKey}`}>
    <div className="w-20 h-20 rounded-2xl bg-slate-100 flex items-center justify-center mb-6">
      <Lock className="text-slate-400" size={36} />
    </div>
    <h3 className="text-xl font-semibold text-slate-800 mb-2">
      {sectionLabel || 'This Section'} is Locked
    </h3>
    <p className="text-slate-500 max-w-md mb-6">
      Access to this section has been restricted by your platform administrator. 
      This may be due to your current plan or agency configuration.
    </p>
    <div className="flex items-center gap-3">
      <Button 
        className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white"
        data-testid="upgrade-add-features-btn"
      >
        <Sparkles size={16} className="mr-2" />
        Upgrade / Add Features
      </Button>
      <Button variant="outline" className="text-slate-600">
        Contact Support
      </Button>
    </div>
  </div>
);

/**
 * Hidden Section Handler
 */
const HiddenSectionHandler = ({ sectionKey, onNavigate }) => (
  <div className="flex flex-col items-center justify-center py-16 px-8 text-center" data-testid={`hidden-section-${sectionKey}`}>
    <div className="w-20 h-20 rounded-2xl bg-red-50 flex items-center justify-center mb-6">
      <AlertTriangle className="text-red-400" size={36} />
    </div>
    <h3 className="text-xl font-semibold text-slate-800 mb-2">
      Section Not Available
    </h3>
    <p className="text-slate-500 max-w-md mb-6">
      This section is not available in your current configuration. 
      Please contact your platform administrator if you believe this is an error.
    </p>
    <Button 
      variant="outline" 
      onClick={() => onNavigate('profile')}
      className="text-slate-600"
    >
      <ArrowLeft size={16} className="mr-2" />
      Return to Agency Profile
    </Button>
  </div>
);

/**
 * Read-Only Banner
 */
const ReadOnlyBanner = () => (
  <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-6 flex items-center gap-3" data-testid="read-only-banner">
    <Eye className="text-blue-500" size={18} />
    <div>
      <p className="text-sm font-medium text-blue-800">View Only Mode</p>
      <p className="text-xs text-blue-600">
        This section is read-only. Contact your platform administrator to make changes.
      </p>
    </div>
  </div>
);

/**
 * Agency Settings Page - Content Only (No Internal Sidebar)
 * 
 * The sidebar is now handled by AgencyWorkspaceLayout in settings mode.
 * This component only renders the settings content for the active section.
 */
const AgencyWorkspaceSettingsPage = () => {
  const { agencyId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { supportContext, isInSupportMode } = useSupportContext();
  
  // Get context from layout
  const outletContext = useOutletContext() || {};
  const { 
    agency: contextAgency, 
    settingsControl: contextSettingsControl,
    activeSettingsSection = 'profile',
    navigateToSection,
    isPlatformAdmin: contextIsPlatformAdmin,
  } = outletContext;
  
  const [agency, setAgency] = useState(contextAgency || null);
  const [entitlements, setEntitlements] = useState({});
  const [settingsControl, setSettingsControl] = useState(contextSettingsControl || {});
  const [internalOverrides, setInternalOverrides] = useState({});
  const [isInternalAgency, setIsInternalAgency] = useState(false);
  const [loading, setLoading] = useState(!contextAgency);
  const [error, setError] = useState(null);
  
  // Determine if read-only mode (support without edit permission)
  const isReadOnly = isInSupportMode && supportContext?.is_read_only !== false;
  
  // Is platform owner or super admin?
  const isPlatformAdmin = contextIsPlatformAdmin || user?.role === 'platform_owner' || user?.role === 'super_admin';
  
  const headers = { Authorization: `Bearer ${token}` };
  
  // Fetch agency data if not provided by context
  useEffect(() => {
    if (contextAgency) {
      setAgency(contextAgency);
      setLoading(false);
      return;
    }
    
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch agency profile via Agency-authorized endpoint (not owner-console).
        const agencyRes = await axios.get(`${API}/admin/agencies/${agencyId}/profile`, {
          headers,
          withCredentials: true,
        });
        setAgency(agencyRes.data);
        
        // Fetch agency entitlements/membership
        try {
          const membershipRes = await axios.get(`${API}/admin/agencies/${agencyId}/membership`, { headers });
          const membership = membershipRes.data;
          
          const entMap = {};
          if (membership?.plan_entitlements) {
            membership.plan_entitlements.forEach(ent => {
              entMap[ent.entitlement_key] = ent.access_mode || 'full_access';
            });
          }
          setEntitlements(entMap);
        } catch (err) {
          setEntitlements({});
        }
        
        // Fetch internal overrides (for internal agency test access)
        try {
          const overridesRes = await axios.get(`${API}/admin/agencies/${agencyId}/internal-overrides`, { headers });
          setIsInternalAgency(overridesRes.data?.is_internal || false);
          setInternalOverrides(overridesRes.data?.internal_overrides || {});
        } catch (err) {
          setIsInternalAgency(false);
          setInternalOverrides({});
        }
        
      } catch (err) {
        console.error('Failed to fetch agency settings:', err);
        setError(err.response?.data?.detail || 'Failed to load agency settings');
      } finally {
        setLoading(false);
      }
    };
    
    if (token && agencyId) {
      fetchData();
    }
  }, [token, agencyId, contextAgency]);
  
  // Sync settings control from context
  useEffect(() => {
    if (contextSettingsControl) {
      setSettingsControl(contextSettingsControl);
    }
  }, [contextSettingsControl]);
  
  // Section key to settings control key mapping
  const sectionToControlKey = {
    'profile': 'agency_profile',
    'billing': 'billing_membership',
    'staff': 'my_staff',
    'portal-access': 'portal_access',
    'portals-mobile': 'portals_mobile_app',
    'branding': 'branding_white_label',
    'domains': 'domains_redirects',
    'communication': 'communication_settings',
    'custom-fields': 'custom_fields',
    'custom-values': 'custom_values',
    'integrations': 'integrations',
    'private-integrations': 'private_integrations',
    'audit-logs': 'audit_logs',
    'labs': 'advanced_labs',
  };
  
  const getControlMode = (sectionKey) => {
    const controlKey = sectionToControlKey[sectionKey];
    return settingsControl[controlKey] || 'editable';
  };
  
  const isSectionVisible = (sectionKey) => {
    if (isPlatformAdmin) return true;
    const mode = getControlMode(sectionKey);
    return mode !== 'hidden';
  };
  
  const isSectionLocked = (sectionKey) => {
    if (isPlatformAdmin) return false;
    const mode = getControlMode(sectionKey);
    return mode === 'locked';
  };
  
  const isSectionReadOnly = (sectionKey) => {
    if (isPlatformAdmin) return false;
    const mode = getControlMode(sectionKey);
    return mode === 'read_only';
  };
  
  // Handle navigation (use context function if available, otherwise update URL)
  const handleNavigateToSection = (sectionKey) => {
    if (navigateToSection) {
      navigateToSection(sectionKey);
    } else {
      navigate(`/agency/${agencyId}/settings?section=${sectionKey}`);
    }
  };
  
  // Render active section content
  const renderSectionContent = () => {
    const sectionLabel = SECTION_LABELS[activeSettingsSection] || 'Settings';
    
    if (!isPlatformAdmin && !isSectionVisible(activeSettingsSection)) {
      return <HiddenSectionHandler sectionKey={activeSettingsSection} onNavigate={handleNavigateToSection} />;
    }
    
    if (!isPlatformAdmin && isSectionLocked(activeSettingsSection)) {
      return <LockedSectionOverlay sectionKey={activeSettingsSection} sectionLabel={sectionLabel} />;
    }
    
    const sectionIsReadOnly = isReadOnly || (!isPlatformAdmin && isSectionReadOnly(activeSettingsSection));
    
    const commonProps = {
      agency,
      agencyId,
      entitlements,
      isReadOnly: sectionIsReadOnly,
      isPlatformAdmin,
      internalOverrides,
      isInternalAgency,
      onUpdate: (updatedAgency) => setAgency({ ...agency, ...updatedAgency }),
      navigateToSection: handleNavigateToSection,
    };
    
    const wrapWithReadOnlyBanner = (content) => {
      if (sectionIsReadOnly && !isReadOnly) {
        return (
          <>
            <ReadOnlyBanner />
            {content}
          </>
        );
      }
      return content;
    };
    
    switch (activeSettingsSection) {
      case 'profile':
        return wrapWithReadOnlyBanner(<AgencyProfileSection {...commonProps} />);
      case 'billing':
        return wrapWithReadOnlyBanner(<BillingMembershipSection {...commonProps} />);
      case 'staff':
        return wrapWithReadOnlyBanner(<MyStaffSection {...commonProps} />);
      case 'portal-access':
        return wrapWithReadOnlyBanner(<PortalAccessSection {...commonProps} />);
      case 'portals-mobile':
        return wrapWithReadOnlyBanner(<PortalsMobileAppSection {...commonProps} />);
      case 'branding':
        return wrapWithReadOnlyBanner(<BrandingWhiteLabelSection {...commonProps} />);
      case 'domains':
        return wrapWithReadOnlyBanner(<DomainsRedirectsSection {...commonProps} />);
      case 'communication':
        return wrapWithReadOnlyBanner(<CommunicationSettingsSection {...commonProps} />);
      case 'custom-fields':
        return wrapWithReadOnlyBanner(<CustomFieldsSection {...commonProps} />);
      case 'custom-values':
        return wrapWithReadOnlyBanner(<CustomValuesSection {...commonProps} />);
      case 'integrations':
        return wrapWithReadOnlyBanner(<IntegrationsSection {...commonProps} />);
      case 'private-integrations':
        return wrapWithReadOnlyBanner(<PrivateIntegrationsSection {...commonProps} />);
      case 'audit-logs':
        return wrapWithReadOnlyBanner(<AuditLogsSection {...commonProps} />);
      case 'labs':
        return wrapWithReadOnlyBanner(<AdvancedLabsSection {...commonProps} />);
      default:
        return wrapWithReadOnlyBanner(<AgencyProfileSection {...commonProps} />);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700 flex items-center gap-3">
          <AlertTriangle size={24} />
          <div>
            <p className="font-medium">Failed to load agency settings</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div data-testid="agency-workspace-settings-page">
      {/* Settings Header Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 mb-6">
        <div className="px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
              <Settings2 className="text-white" size={28} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                {SECTION_LABELS[activeSettingsSection] || 'Agency Settings'}
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">
                Configure agency settings and preferences
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Badge className="bg-emerald-100 text-emerald-700 border-0 px-3 py-1.5">
              <Sparkles size={14} className="mr-1.5" />
              Agency Workspace
            </Badge>
            {!isReadOnly && (
              <Button
                size="sm"
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                <Save size={16} className="mr-2" />
                Save Changes
              </Button>
            )}
          </div>
        </div>
        
        {/* Access Order Info */}
        <div className="px-6 py-3 bg-gradient-to-r from-slate-50 to-amber-50/30 border-t border-slate-100">
          <p className="text-sm text-slate-600">
            <span className="font-medium">Access order:</span> Plan entitlement → Agency settings access → Role permission
          </p>
        </div>
      </div>
      
      {/* Section Content - scrollable for long profile sections */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200">
        <div className="p-6 pb-12 overflow-y-auto">
          {renderSectionContent()}
        </div>
      </div>
    </div>
  );
};

export default AgencyWorkspaceSettingsPage;
